import { Component, computed, ElementRef, inject, viewChild } from '@angular/core';
import { CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { MapWallDisplayFacade } from '../../../store/map-wall-display.facade';
import { ImageCardSettings, WallDisplayConfiguration } from '../../../models';
import { ImageCardComponent } from './image-card/image-card.component';

@Component({
    selector: 'topo-image-card-widget',
    imports: [CdkDropList, ImageCardComponent],
    templateUrl: './image-card-widget.component.html',
    styleUrl: './image-card-widget.component.scss'
})
export class ImageCardWidgetComponent {
  protected state = inject(MapWallDisplayFacade);
  protected settings = this.state.imageCardSettings;
  protected showWidget = computed(() => {
    const settings = this.settings();
    const isActive = this.state.showImageCardWidget();
    return isActive && !!settings.imageDataUrl;
  });

  private dropArea = viewChild<ElementRef<HTMLDivElement>>('dropArea');
  protected dropAreaRect = computed(() => this.dropArea()?.nativeElement?.getBoundingClientRect());
  private previousRect: DOMRect;

  // set the previous position data before CDK drop animations occur to accurately calculate new positioning in handleDrop function
  protected onDragReleased() {
    const previousEl = this.dropArea()?.nativeElement?.querySelector('.image-card.cdk-drag-placeholder');
    this.previousRect = previousEl?.getBoundingClientRect();
  }

  protected handleDrop({ distance }: CdkDragDrop<void>): void {
    // avoids using "item" from CdkDragDrop event because it has updated DOM values.
    // get the element's previous bounding rect on drag release event since it fires before list drop
    if (!this.previousRect) {
      return;
    }

    const dropAreaRect = this.dropArea().nativeElement.getBoundingClientRect();
    const rectForCalc = this.previousRect;

    const top = rectForCalc.top + distance.y - dropAreaRect.top;
    const bottom = (rectForCalc.bottom + distance.y - dropAreaRect.bottom) * -1;
    const left = rectForCalc.left + distance.x - dropAreaRect.left;
    const right = (rectForCalc.right + distance.x - dropAreaRect.right) * -1;

    // converting to percentage based off container for screen scalability
    const position: ImageCardSettings['position'] = {
      top: (Math.max(top, 0) / dropAreaRect.height) * 100,
      bottom: (Math.max(bottom, 0) / dropAreaRect.height) * 100,
      left: (Math.max(left, 0) / dropAreaRect.width) * 100,
      right: (Math.max(right, 0) / dropAreaRect.width) * 100
    };

    const configuration = this.state.configuration();

    const configWithUpdates: WallDisplayConfiguration = {
      ...configuration,
      imageCardSettings: {
        ...configuration.imageCardSettings,
        position
      }
    };

    this.state.updateConfiguration(configWithUpdates);
  }
}
